import { stat } from "node:fs/promises";
import path from "node:path";

import {
  PRIMEUI_PROJECT_DIR_NAME,
  PrimeUiProjectConfigError,
  resolvePrimeUiApiBaseUrlDetails,
  resolvePrimeUiApiKeyDetails,
  resolvePrimeUiProjectConfig,
  resolvePrimeUiTargetProjectRoot,
} from "../lib/project-link-config.js";
import { packageMetadata } from "../package-metadata.js";
import {
  ApiProjectDataProvider,
  DEFAULT_API_BASE_URL,
  buildPrimeUiApiUrl,
  normalizePrimeUiApiRoot,
} from "../sources/api-provider.js";
import { buildHealthHints } from "./hints.js";
import type {
  HealthApiKeyComparison,
  HealthProjectConfigStatus,
  HealthProjectRootComparison,
  HealthReportData,
  RunHealthCheckOptions,
} from "./index.js";
import { findNestedPrimeUiCandidates } from "./nested-primeui-search.js";
import { formatHealthReport } from "./report.js";
import { checkLatestPackageVersion } from "./update-check.js";

function mapProjectSource(
  source: string | undefined,
): HealthReportData["projectResolution"]["effectiveSource"] {
  switch (source) {
    case "tool":
      return "input";
    case "env":
      return "env";
    case "search":
      return "search";
    default:
      return "unresolved";
  }
}

function mapProjectErrorSource(
  source: string | undefined,
): HealthReportData["projectResolution"]["errorSource"] {
  switch (source) {
    case "tool":
      return "input";
    case "env":
      return "env";
    case "search":
      return "search";
    default:
      return undefined;
  }
}

function compareProjectRoots(
  inputPath?: string,
  envPath?: string,
): HealthProjectRootComparison {
  if (inputPath && envPath) {
    return inputPath === envPath ? "same" : "different";
  }
  if (inputPath) {
    return "input-only";
  }
  if (envPath) {
    return "env-only";
  }
  return "neither";
}

function compareApiKeys(
  valuesMatch: boolean | undefined,
  envProvided: boolean,
  configProvided: boolean,
): HealthApiKeyComparison {
  if (envProvided && configProvided) {
    return valuesMatch ? "same" : "different";
  }
  return "not-comparable";
}

function mapConfigStatus(
  error: PrimeUiProjectConfigError | undefined,
): HealthProjectConfigStatus {
  if (!error) {
    return "valid";
  }

  switch (error.code) {
    case "PROJECT_CONFIG_NOT_FOUND":
      return typeof error.details.expectedConfigPath === "string"
        ? "missing"
        : "unresolved";
    case "PROJECT_CONFIG_INVALID_JSON":
      return "invalid-json";
    case "PROJECT_CONFIG_INVALID_FORMAT":
      return "invalid-format";
    case "PROJECT_CONFIG_READ_FAILED":
      return "read-failed";
    default:
      return "unresolved";
  }
}

async function directoryExists(targetPath: string): Promise<boolean> {
  try {
    const targetStat = await stat(targetPath);
    return targetStat.isDirectory();
  } catch {
    return false;
  }
}

function classifyApiError(message: string): "auth" | "network" {
  return /PrimeUI API request failed \((401|403)\)/.test(message)
    ? "auth"
    : "network";
}

export async function runHealthCheck(
  options: RunHealthCheckOptions,
): Promise<number> {
  const metadata = options.metadata ?? packageMetadata;
  const env = options.env ?? process.env;
  const cwd = path.resolve(options.cwd ?? process.cwd());
  const inputProjectRoot = options.projectRoot?.trim()
    ? path.resolve(options.projectRoot.trim())
    : undefined;
  const envProjectRoot = env.PRIMEUI_PROJECT_ROOT?.trim()
    ? path.resolve(env.PRIMEUI_PROJECT_ROOT.trim())
    : undefined;

  const versionResult = await (
    options.checkLatestVersion ?? checkLatestPackageVersion
  )(metadata.name, metadata.version);

  let resolvedProjectConfig:
    | Awaited<ReturnType<typeof resolvePrimeUiProjectConfig>>
    | undefined;
  let projectConfigError: PrimeUiProjectConfigError | undefined;

  try {
    resolvedProjectConfig = await resolvePrimeUiProjectConfig({
      cwd,
      projectRootFromTool: inputProjectRoot,
      projectRootFromEnv: envProjectRoot,
    });
  } catch (error) {
    if (!(error instanceof PrimeUiProjectConfigError)) {
      throw error;
    }
    projectConfigError = error;
  }

  const resolvedProjectRoot =
    resolvedProjectConfig?.projectRoot ??
    (typeof projectConfigError?.details.projectRoot === "string"
      ? path.resolve(projectConfigError.details.projectRoot)
      : undefined);
  const primeUiDirPath = resolvedProjectRoot
    ? path.join(resolvedProjectRoot, PRIMEUI_PROJECT_DIR_NAME)
    : inputProjectRoot
      ? path.join(inputProjectRoot, PRIMEUI_PROJECT_DIR_NAME)
      : envProjectRoot &&
          mapProjectErrorSource(
            projectConfigError?.details.source as string,
          ) === "env"
        ? path.join(envProjectRoot, PRIMEUI_PROJECT_DIR_NAME)
        : undefined;
  const primeUiDirStatus = primeUiDirPath
    ? (await directoryExists(primeUiDirPath))
      ? "found"
      : "missing"
    : "unresolved";
  const configStatus = resolvedProjectConfig
    ? "valid"
    : mapConfigStatus(projectConfigError);
  const configPath =
    resolvedProjectConfig?.projectConfigPath ??
    (typeof projectConfigError?.details.expectedConfigPath === "string"
      ? path.resolve(projectConfigError.details.expectedConfigPath)
      : primeUiDirPath
        ? path.join(primeUiDirPath, "project.json")
        : undefined);
  const nestedSearchPerformed =
    Boolean(inputProjectRoot) && primeUiDirStatus === "missing";
  const nestedCandidates = nestedSearchPerformed
    ? await findNestedPrimeUiCandidates(inputProjectRoot!)
    : [];

  const apiKeyDetails = resolvePrimeUiApiKeyDetails({
    projectConfig: resolvedProjectConfig?.projectConfig,
    apiKeyFromEnv: env.PRIMEUI_API_KEY,
  });
  const configApiKeyState = resolvedProjectConfig
    ? "set"
    : configStatus === "missing" || configStatus === "unresolved"
      ? "missing"
      : "unknown";

  const apiBaseUrlDetails = resolvePrimeUiApiBaseUrlDetails({
    projectConfig: resolvedProjectConfig?.projectConfig,
    baseUrlFromEnv: env.PRIMEUI_API_BASE_URL,
  });
  const apiBaseUrlSource = apiBaseUrlDetails.source;
  const defaultApiRoot = normalizePrimeUiApiRoot(DEFAULT_API_BASE_URL);
  let apiRoot: string | undefined;
  let projectInfoUrl: string | undefined;
  let apiCheck: HealthReportData["apiCheck"] | undefined;

  try {
    apiRoot = normalizePrimeUiApiRoot(apiBaseUrlDetails.baseUrl);
    projectInfoUrl = buildPrimeUiApiUrl(apiRoot, "project");
  } catch (error) {
    apiCheck = {
      status: "skipped",
      apiBaseUrlSource,
      nonStandardBaseUrl: Boolean(apiBaseUrlDetails.baseUrl?.trim()),
      message: error instanceof Error ? error.message : String(error),
      errorKind: "configuration",
    };
  }

  if (!apiCheck) {
    if (!apiKeyDetails.apiKey) {
      apiCheck = {
        status: "skipped",
        apiRoot,
        projectInfoUrl,
        apiBaseUrlSource,
        nonStandardBaseUrl: apiRoot !== defaultApiRoot,
        message:
          "No usable API key was available, so project info was not requested.",
      };
    } else {
      const provider =
        options.createProvider?.({
          apiKey: apiKeyDetails.apiKey,
          baseUrl: apiBaseUrlDetails.baseUrl,
        }) ??
        new ApiProjectDataProvider({
          apiKey: apiKeyDetails.apiKey,
          baseUrl: apiBaseUrlDetails.baseUrl,
        });

      try {
        await provider.getProjectInfo();
        apiCheck = {
          status: "passed",
          apiRoot,
          projectInfoUrl,
          apiBaseUrlSource,
          nonStandardBaseUrl: apiRoot !== defaultApiRoot,
          message: "Project info request succeeded with the current API key.",
        };
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        apiCheck = {
          status: "failed",
          apiRoot,
          projectInfoUrl,
          apiBaseUrlSource,
          nonStandardBaseUrl: apiRoot !== defaultApiRoot,
          message,
          errorKind: classifyApiError(message),
        };
      }
    }
  }

  if (!apiCheck) {
    throw new Error(
      "PrimeUI health check did not produce an API check result.",
    );
  }

  const report: HealthReportData = {
    version: {
      currentVersion: metadata.version,
      latestVersion: versionResult.latestVersion,
      updateStatus: versionResult.status,
      detail: versionResult.detail,
    },
    runtimeContext: {
      cwd,
      inputProjectRoot,
      envProjectRoot,
      envApiBaseUrl: env.PRIMEUI_API_BASE_URL?.trim() || undefined,
      envApiKeySet: Boolean(env.PRIMEUI_API_KEY?.trim()),
    },
    sourceResolution: {
      projectRoot: {
        inputPath: inputProjectRoot,
        envPath: envProjectRoot,
        comparison: compareProjectRoots(inputProjectRoot, envProjectRoot),
        effectiveSource: mapProjectSource(
          resolvedProjectConfig?.source ??
            (projectConfigError?.details.source as string | undefined),
        ),
      },
      apiKey: {
        envState: apiKeyDetails.envProvided ? "set" : "missing",
        configState: configApiKeyState,
        comparison: compareApiKeys(
          apiKeyDetails.valuesMatch,
          apiKeyDetails.envProvided,
          apiKeyDetails.configProvided,
        ),
        effectiveSource: apiKeyDetails.source,
      },
    },
    projectResolution: {
      resolvedProjectRoot,
      effectiveSource: mapProjectSource(
        resolvedProjectConfig?.source ??
          (projectConfigError?.details.source as string | undefined),
      ),
      errorSource: mapProjectErrorSource(
        projectConfigError?.details.source as string | undefined,
      ),
      primeUiDirPath,
      primeUiDirStatus,
      configPath,
      configStatus,
      targetProjectPath: resolvedProjectConfig?.projectConfig.targetProjectPath,
      targetProjectRoot: resolvedProjectConfig
        ? resolvePrimeUiTargetProjectRoot(
            resolvedProjectConfig.projectRoot,
            resolvedProjectConfig.projectConfig,
          )
        : undefined,
      errorCode: projectConfigError?.code,
      errorMessage: projectConfigError?.message,
      inputProjectRootValidated:
        Boolean(inputProjectRoot) && resolvedProjectConfig?.source === "tool",
    },
    nestedSearch: {
      performed: nestedSearchPerformed,
      rootPath: nestedSearchPerformed ? inputProjectRoot : undefined,
      candidates: nestedCandidates,
    },
    apiCheck,
    hints: [],
  };

  report.hints = buildHealthHints({
    report,
    connectApiKey:
      nestedSearchPerformed &&
      nestedCandidates.length === 0 &&
      apiCheck.status === "passed"
        ? apiKeyDetails.apiKey
        : undefined,
  });

  options.stdout.write(`${formatHealthReport(report)}\n`);
  return 0;
}
